/**
 * Progress as a string a person can keep.
 *
 * The vault (vault.ts) keeps spare copies inside the browser, and an account
 * keeps one on the server. Neither covers the case this file exists for: a
 * learner who never signs in and then clears everything this site stored,
 * on purpose or by clearing "cookies and site data" for something else. The
 * browser deleted exactly what it promised to delete, and it was right to.
 * The only honest answer is a copy that lives somewhere the browser does not
 * own, which means one the learner has taken out themselves.
 *
 * So: a code. `exportCode` turns the record into one line of text, and
 * `importCode` turns that line back into a record in any browser. It is the
 * same signed blob localStorage holds, gzipped where the platform has
 * CompressionStream and plain where it does not, base64url so it survives
 * being pasted through a chat window or an email. Twenty cleared levels come
 * to a few hundred characters compressed.
 *
 * A code is not a credential. It carries no identity, unlocks no account and
 * is worth nothing to anyone else; the worst it can do in another person's
 * hands is tell them which levels its owner has cleared. It is signed for
 * the same reason the stored blob is signed — so that a hand-edited code is
 * *noticed*, not so that it is refused. An import that fails its signature
 * is still imported, with every clear in it marked unverified, exactly as a
 * hand-edited localStorage blob would be. See docs/cheating.md: a learner who
 * forges a code has cheated only themselves, and the design would rather let
 * that happen than turn away one honest person holding a code their own
 * browser wrote.
 */
import { markTampered, signProgress, type Progress } from '../progress';
import { getStrings } from './strings';

/** Compressed, then base64url. The common case. */
const GZIP_PREFIX = 'EOS1.';
/** Plain JSON, then base64url. For a browser without CompressionStream. */
const PLAIN_PREFIX = 'EOS0.';

/**
 * A ceiling on what `importCode` will even try to decode. A progress record
 * is a few kilobytes; this is far above any real one and far below a size
 * that could hurt, and it is what stops a pasted novel from being inflated
 * into memory.
 */
const MAX_CODE_CHARS = 64_000;
/** The same ceiling on the far side of the decompressor, where a small code can become a large string. */
const MAX_JSON_CHARS = 256_000;

/** The blob a code carries: the record, plus the signature the stored copy would have. */
interface CodeBody extends Progress {
  sig: string;
}

/* ── base64url ───────────────────────────────────────────────────────────── */

function toBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(text: string): Uint8Array | null {
  try {
    const padded = text.replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
  } catch {
    return null;
  }
}

/* ── gzip ────────────────────────────────────────────────────────────────── */

async function gzip(text: string): Promise<Uint8Array | null> {
  try {
    if (typeof CompressionStream === 'undefined') return null;
    const stream = new Blob([text]).stream().pipeThrough(new CompressionStream('gzip'));
    return new Uint8Array(await new Response(stream).arrayBuffer());
  } catch {
    return null;
  }
}

async function gunzip(bytes: Uint8Array): Promise<string | null> {
  try {
    if (typeof DecompressionStream === 'undefined') return null;
    const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(new DecompressionStream('gzip'));
    const text = await new Response(stream).text();
    return text.length > MAX_JSON_CHARS ? null : text;
  } catch {
    return null;
  }
}

/* ── Out ─────────────────────────────────────────────────────────────────── */

/**
 * The record as one line of text. Never rejects: a browser that cannot
 * compress gets the plain form, which is longer and works everywhere.
 */
export async function exportCode(p: Progress): Promise<string> {
  const body: CodeBody = { version: p.version, xp: p.xp, levels: p.levels, lastClear: p.lastClear, sig: signProgress(p) };
  const json = JSON.stringify(body);
  const packed = await gzip(json);
  if (packed) return GZIP_PREFIX + toBase64Url(packed);
  return PLAIN_PREFIX + toBase64Url(new TextEncoder().encode(json));
}

/**
 * What a downloaded backup is called. Dated, because a learner may keep several.
 *
 * ASCII in both languages, deliberately. This is a filename: it is typed at a
 * shell, tab-completed, attached to mail and read back over a phone, and an
 * Arabic one would arrive percent-encoded in half the places it travels and
 * as mojibake in the other half. The date is Western digits for the same
 * reason every other number in this product is (lib/format.ts).
 */
export function backupFilename(now = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `engineeros-progress-${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}.txt`;
}

/* ── In ──────────────────────────────────────────────────────────────────── */

export type ImportResult =
  | {
      ok: true;
      progress: Progress;
      /**
       * False when the code did not match its signature. The record is still
       * returned and still counts; every clear in it has been marked
       * unverified, and the caller should say so rather than celebrate.
       */
      signed: boolean;
    }
  | { ok: false; reason: string };

/**
 * A code back into a record. The caller merges the result into what this
 * browser already has (`mergeProgress`) rather than replacing it, so an
 * import can only ever add.
 *
 * ## Why `reason` is a whole sentence
 *
 * It used to be a lowercase fragment — `it was empty` — that the dialog
 * completed by writing "That code could not be read: " in front of it. That
 * works in exactly one language. Arabic does not put the clause in that
 * position, and a lowercase fragment is not a thing a translator can
 * translate: on its own it means nothing, and there is no guarantee the
 * sentence it is going to be dropped into still has a hole in that shape.
 *
 * So each reason is now a finished sentence from the catalogue, chosen here
 * and printed by the dialog as it is. This module is not a component, so it
 * reads the catalogue with `getStrings()` rather than the hook — see the
 * comment on `getStrings` in lib/strings/index.ts. The sentence is built at
 * the moment of the failure, which is the moment it is about to be shown,
 * so it is in whatever language the page is in then.
 */
export async function importCode(code: string): Promise<ImportResult> {
  const failed = getStrings().standing.backup.failed;
  const trimmed = code.trim().replace(/\s+/g, '');
  if (!trimmed) return { ok: false, reason: failed.empty };
  if (trimmed.length > MAX_CODE_CHARS) return { ok: false, reason: failed.tooLong };

  const gzipped = trimmed.startsWith(GZIP_PREFIX);
  if (!gzipped && !trimmed.startsWith(PLAIN_PREFIX)) {
    return { ok: false, reason: failed.wrongPrefix(GZIP_PREFIX, PLAIN_PREFIX) };
  }

  const bytes = fromBase64Url(trimmed.slice(GZIP_PREFIX.length));
  if (!bytes) return { ok: false, reason: failed.notBase64 };

  const json = gzipped ? await gunzip(bytes) : decodeUtf8(bytes);
  if (json === null) return { ok: false, reason: gzipped ? failed.notCompressed : failed.notText };

  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return { ok: false, reason: failed.notARecord };
  }

  const shaped = validate(parsed);
  if (typeof shaped === 'string') return { ok: false, reason: shaped };

  const { progress, sig } = shaped;
  const signed = sig === signProgress(progress);
  // Not from the catalogue, and this is the one string here that is not.
  // A plausibility flag's `detail` is *stored*: it goes into the record, into
  // localStorage, into the next exported code and across to the account, and
  // it is merged with flags written on other devices. Translating it at the
  // moment of writing would freeze one language into the data — a record
  // written in Arabic would keep saying so in an English browser for as long
  // as the level stayed unverified. The detail is written by the grader in
  // English (engine/runtime.ts) for the same reason; localising it belongs
  // with whoever makes flags render from their `code` rather than their
  // prose, and that is a change to progress.ts and the level list, not here.
  if (!signed) markTampered(progress, 'it was restored from a backup code that did not match its signature');
  return { ok: true, progress, signed };
}

function decodeUtf8(bytes: Uint8Array): string | null {
  try {
    const text = new TextDecoder().decode(bytes);
    return text.length > MAX_JSON_CHARS ? null : text;
  } catch {
    return null;
  }
}

/**
 * The parsed blob, checked field by field, or the sentence saying why it is
 * not a record.
 *
 * This is the same job worker/account.ts does to a pushed record and for the
 * same reason: the bytes came from outside and nothing downstream should have
 * to wonder. It is deliberately forgiving about *values* — a code claiming a
 * million XP is imported, and the signature check is what notices — and
 * unforgiving about *shape*, because a wrong shape is what crashes a render.
 */
function validate(parsed: unknown): { progress: Progress; sig: string } | string {
  const failed = getStrings().standing.backup.failed;
  if (!isRecord(parsed)) return failed.notARecord;
  if (parsed.version !== 2) return failed.wrongVersion;
  if (!isRecord(parsed.levels)) return failed.badLevels;
  if (typeof parsed.sig !== 'string') return failed.noSignature;

  // A warning to anyone editing the object built below: the signature is a
  // digest of JSON.stringify output, which carries field order, and this
  // rebuilds each level field by field rather than validating it in place.
  // So these fields must stay in the order LevelProgress declares them. Put
  // one of them in a different place and every honest code starts importing
  // as tampered, with no error anywhere to say why. (worker/account.ts
  // validates against a key allowlist without rebuilding, which is why the
  // same trap does not exist there.)
  const levels: Progress['levels'] = {};
  for (const [id, value] of Object.entries(parsed.levels)) {
    if (typeof id !== 'string' || !id || id.length > 200) return failed.badLevelName;
    // Sliced here rather than in the catalogue: how much of a level id is
    // safe to print back is this module's judgement about bytes from
    // outside, not a question about words.
    if (!isRecord(value)) return failed.badLevelRecord(id.slice(0, 40));
    const stars = num(value.stars);
    levels[id] = {
      completed: value.completed === true,
      stars: (stars !== null && stars >= 0 && stars <= 3 ? Math.round(stars) : 0) as 0 | 1 | 2 | 3,
      hintsUsed: Math.max(0, num(value.hintsUsed) ?? 0),
      attempts: Math.max(0, num(value.attempts) ?? 0),
      completedAt: num(value.completedAt),
      xpEarned: Math.max(0, num(value.xpEarned) ?? 0),
      verified: value.verified === true,
      ...(Array.isArray(value.flags) ? { flags: flagsOf(value.flags) } : {}),
    };
  }

  const xp = num(parsed.xp);
  const progress: Progress = {
    version: 2,
    xp: xp !== null && xp >= 0 ? xp : 0,
    levels,
    lastClear: clockOf(parsed.lastClear),
  };
  return { progress, sig: parsed.sig };
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function flagsOf(raw: unknown[]): Progress['levels'][string]['flags'] {
  const out: NonNullable<Progress['levels'][string]['flags']> = [];
  for (const f of raw) {
    if (!isRecord(f) || typeof f.code !== 'string' || typeof f.detail !== 'string') continue;
    out.push({ code: f.code as NonNullable<Progress['levels'][string]['flags']>[number]['code'], detail: f.detail.slice(0, 300) });
  }
  return out;
}

function clockOf(v: unknown): Progress['lastClear'] {
  if (!isRecord(v)) return null;
  const wall = num(v.wall);
  const mono = num(v.mono);
  const origin = num(v.origin);
  if (wall === null || mono === null || origin === null) return null;
  return { wall, mono, origin, ...(typeof v.missionId === 'string' ? { missionId: v.missionId } : {}) };
}
